'use client'

// components/Ads/AdSlot.jsx
//
// Slot de publicidad reutilizable (template). Port de assetto-gallery
// (src/components/ads/AdSlot.tsx) a React + styled-components.
//
// Para cambiar lo que muestra un slot EN EL FUTURO: editá su entrada en
// AD_SLOTS — poné `creativeUrl` (la imagen del anunciante) y `href` (a dónde
// lleva el click). Nada más. Sin `creativeUrl` se muestra el placeholder SVG.
//
// Para tener MÁS slots: agregá otra entrada acá y soltá un
// <AdSlot slot="nombre" /> donde quieras. El mismo componente mide
// impresiones y clicks por slot (ad_impression / ad_click), así el dashboard
// admin calcula CTR por ubicación.

import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { track } from '@/utils/analytics'

export const AD_SLOTS = {
    // Slot de ejemplo (sidebar izquierdo). De momento sin imagen → placeholder,
    // y el link va a config-strike.com solo para testear el flujo de
    // impresión/click.
    'sidebar-left': {
        href: 'https://config-strike.com',
        // creativeUrl: '/ads/sidebar-left.png', // ← en el futuro: poné la imagen y listo
        alt: 'Espacio publicitario',
    },
}

const SlotLink = styled.a`
    display: block;
    width: 100%;
    border-radius: 8px;
    overflow: hidden;
    text-decoration: none;
    line-height: 0;
    transition: transform 0.12s ease, box-shadow 0.12s ease;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
    }
`

const SlotImg = styled.img`
    display: block;
    width: 100%;
    height: auto;
`

const SlotSvg = styled.svg`
    display: block;
    width: 100%;
    height: auto;
`

/** Placeholder propio — banner "tu marca acá", solo para ver el slot y probar
 *  el tracking hasta que entre una creatividad real. */
function PlaceholderCreative() {
    return (
        <SlotSvg viewBox="0 0 300 90" role="img" aria-label="Espacio publicitario disponible">
            <defs>
                <linearGradient id="csAdBg" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#1b1420" />
                    <stop offset="1" stopColor="#120e18" />
                </linearGradient>
            </defs>
            <rect x="1" y="1" width="298" height="88" rx="9" fill="url(#csAdBg)" stroke="#332a3c" strokeWidth="1" />
            <rect
                x="8"
                y="8"
                width="284"
                height="74"
                rx="6"
                fill="none"
                stroke="#463a52"
                strokeWidth="1.5"
                strokeDasharray="5 5"
                opacity="0.5"
            />
            <text x="150" y="34" textAnchor="middle" fill="#a390b8" fontSize="9" letterSpacing="2" fontFamily="system-ui, sans-serif">
                ESPACIO PUBLICITARIO
            </text>
            <text x="150" y="56" textAnchor="middle" fill="#e9a84a" fontSize="17" fontWeight="700" fontFamily="system-ui, sans-serif">
                Tu marca acá
            </text>
            <text x="150" y="72" textAnchor="middle" fill="#786a8a" fontSize="8.5" fontFamily="system-ui, sans-serif">
                publicidad · config-strike.com
            </text>
        </SlotSvg>
    )
}

/**
 * Un slot de publicidad. Registra una impresión (ad_impression) la primera
 * vez que queda ≥50% visible en pantalla, y un click (ad_click) al
 * clickearlo — ambos con el id del slot, para medir CTR por ubicación.
 * Anónimo, como el resto de la telemetría.
 */
export default function AdSlot({ slot, className }) {
    const cfg = AD_SLOTS[slot]
    const ref = useRef(null)
    const impressedRef = useRef(false)

    useEffect(() => {
        if (!cfg || !ref.current || typeof IntersectionObserver === 'undefined') return
        const io = new IntersectionObserver(
            (entries) => {
                for (const e of entries) {
                    if (!impressedRef.current && e.isIntersecting && e.intersectionRatio >= 0.5) {
                        impressedRef.current = true
                        track('ad_impression', { slot })
                        io.disconnect()
                    }
                }
            },
            { threshold: [0, 0.5] },
        )
        io.observe(ref.current)
        return () => io.disconnect()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slot])

    if (!cfg) return null

    return (
        <SlotLink
            ref={ref}
            className={className}
            href={cfg.href}
            target="_blank"
            rel="noopener noreferrer nofollow sponsored"
            onClick={() => track('ad_click', { slot })}
        >
            {cfg.creativeUrl ? <SlotImg src={cfg.creativeUrl} alt={cfg.alt ?? ''} /> : <PlaceholderCreative />}
        </SlotLink>
    )
}
